import {showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {
  useCancelTelephonyCallMutation,
  useLazyGetTelephonyCallQuery,
  useLazyGetTelephonyCurrentCallQuery,
  useLazyGetTelephonyVoiceTokenQuery,
} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {ApiError, useOnMount} from 'shared/web';

import {TELEPHONY_CALL_ERROR_CODES} from '../constants/TELEPHONY_CALL_ERROR_CODES';
import {BrowserPhoneFailure} from '../types/BrowserPhoneFailure';
import {getTelephonyCallApiErrorCode} from '../utils/getTelephonyCallApiErrorCode';
import {isForbiddenApiError} from '../utils/isForbiddenApiError';
import {useApplyTelephonyCallState} from './useApplyTelephonyCallState';
import {useBrowserPhone} from './useBrowserPhone';

/** Token answers that turn browser calling off for the session instead of retrying later. */
const PERMANENT_TOKEN_ERROR_CODES: string[] = [
  TELEPHONY_CALL_ERROR_CODES.TELEPHONY_NOT_PROVISIONED,
  TELEPHONY_CALL_ERROR_CODES.BROWSER_CALLING_NOT_PROVISIONED,
];

const getFailureMessage = (failure: BrowserPhoneFailure) =>
  match(failure)
    .with('MICROPHONE_BLOCKED', () => i18n.t('page.telephony.browser.microphoneBlocked'))
    .with('NO_MICROPHONE', () => i18n.t('page.telephony.browser.noMicrophone'))
    .with('CONNECTION_FAILED', () => i18n.t('page.telephony.browser.connectionFailed'))
    .exhaustive();

/**
 * Starts the tab's softphone when the signed-in layout mounts (so incoming calls ring this tab)
 * and destroys it when the layout unmounts on logout or workspace switch. Mount once per app; the
 * top-bar indicator does.
 */
export function useBrowserPhoneLifecycle() {
  const {phone} = useBrowserPhone();
  const [getVoiceToken] = useLazyGetTelephonyVoiceTokenQuery();
  const [cancelCall] = useCancelTelephonyCallMutation();
  const [getCurrentCall] = useLazyGetTelephonyCurrentCallQuery();
  const [getCall] = useLazyGetTelephonyCallQuery();
  const applyCallState = useApplyTelephonyCallState();

  const fetchToken = () =>
    getVoiceToken(undefined, false)
      .unwrap()
      .then((response) => response.token)
      .catch((error: ApiError) => {
        const code = getTelephonyCallApiErrorCode(error);

        if (
          isForbiddenApiError(error) ||
          (isNotNil(code) && PERMANENT_TOKEN_ERROR_CODES.includes(code))
        ) {
          return null;
        }

        throw error;
      });

  useOnMount(
    () =>
      phone.start({
        fetchToken,
        // Read the call back instead of waiting for the push, so the indicator ends at once. After a
        // failed cancel the backend's 60 s rule still ends the call.
        cancelCall: (callId) =>
          cancelCall({callId})
            .unwrap()
            .then(() => getCall({callId}, false).unwrap())
            .then(applyCallState)
            .catch(handleApiError),
        onFailure: (failure) => showNotification.error(getFailureMessage(failure)),
        // The SDK can ring before the push that shows the call arrives; read it so Accept shows.
        onIncoming: () => getCurrentCall(undefined, false),
      }),
    phone.stop
  );
}
